import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  CreateNewFolderOutlined,
  FolderOutlined,
  KeyboardArrowDown,
  UploadFileOutlined,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import type { FirmDocumentMeasurementDateListItem } from 'api';
import {
  OverflowTooltip,
  ScrollHiddenItemsHint,
  Skeleton,
} from 'components/ui';
import {
  useScrollHiddenItemCounts,
  useSearchableListKeyboardNavigation,
} from 'components/ui/hooks';
import {
  isKeyboardActivationKey,
  KEYBOARD_ARROW_RIGHT_KEY,
} from 'components/ui/utilities/keyboardNavigation';
import { getCssPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import FirmDocumentsSelectionCheckbox from 'pages/Documents/FirmDocuments/components/FirmDocumentsSelectionCheckbox';
import { isFirmLevelDocumentsId } from 'services/hooks/documents/firmLevelDocuments';
import { getNumberValue, shortDate } from 'utilities';
import {
  MEASUREMENT_DATES_ACCORDION_PANEL_ACTIVE_SCROLL_DELAY_MS,
  MEASUREMENT_DATES_ACCORDION_PANEL_LABELS,
  MEASUREMENT_DATES_ACCORDION_PANEL_NAVIGATION_ITEM_SELECTOR,
  MEASUREMENT_DATES_ACCORDION_PANEL_SCROLL_ITEM_SELECTOR,
  MEASUREMENT_DATES_ACCORDION_PANEL_VISIBLE_INCREMENT,
} from './MeasurementDatesAccordionPanel.constants';
import type {
  MeasurementDatesAccordionPanelProps,
  MeasurementDatesAccordionPanelSelectionState,
} from './MeasurementDatesAccordionPanel.props';
import {
  MeasurementDatesAccordionPanelBody,
  MeasurementDatesAccordionPanelChevron,
  MeasurementDatesAccordionPanelDate,
  MeasurementDatesAccordionPanelHeader,
  MeasurementDatesAccordionPanelIcon,
  MeasurementDatesAccordionPanelInlineAction,
  MeasurementDatesAccordionPanelList,
  MeasurementDatesAccordionPanelListFrame,
  MeasurementDatesAccordionPanelMetadata,
  MeasurementDatesAccordionPanelRoot,
  MeasurementDatesAccordionPanelSection,
  MeasurementDatesAccordionPanelShowMoreButton,
  MeasurementDatesAccordionPanelSkeletonList,
  MeasurementDatesAccordionPanelSkeletonRow,
  MeasurementDatesAccordionPanelSkeletonSection,
  MeasurementDatesAccordionPanelState,
  MeasurementDatesAccordionPanelStateDescription,
  MeasurementDatesAccordionPanelStateTitle,
  MeasurementDatesAccordionPanelTitle,
} from './MeasurementDatesAccordionPanel.styles';
import { MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS } from './MeasurementDatesAccordionPanel.testIds';

type MeasurementDateName = FirmDocumentMeasurementDateListItem;

const getMeasurementDateKey = (measurementDate: MeasurementDateName) =>
  String(measurementDate.id ?? measurementDate.date);

// The Firm Documents row is not a date: it is labelled by name.
const getMeasurementDateLabel = (measurementDate: MeasurementDateName) =>
  isFirmLevelDocumentsId(measurementDate.id)
    ? measurementDate.name
    : (shortDate(measurementDate.date) ?? measurementDate.name);

const getMeasurementDateMetadataLabel = (measurementDate: MeasurementDateName) => (
  MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.measurementDateMetadata({
    documentCount: getNumberValue(measurementDate.document_count),
    pendingRequestCount: getNumberValue(measurementDate.request_counts?.pending),
    sentRequestCount: getNumberValue(measurementDate.request_counts?.sent),
    subfolderCount: getNumberValue(measurementDate.subfolder_count),
    uploadedRequestCount: getNumberValue(measurementDate.request_counts?.uploaded),
  })
);

const DEFAULT_SELECTION_STATE: MeasurementDatesAccordionPanelSelectionState = {
  checked: false,
  disabled: true,
  indeterminate: false,
};

const MeasurementDatesAccordionPanelSkeleton = () => (
  <MeasurementDatesAccordionPanelSkeletonList data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.skeleton}>
    {Array.from({ length: MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.loadingRowCount }, (_, index) => (
      <MeasurementDatesAccordionPanelSkeletonSection key={index}>
        <MeasurementDatesAccordionPanelSkeletonRow>
          <Skeleton
            height={getCssPx(16)}
            radius={getCssPx(4)}
            width={getCssPx(16)}
          />
          <Skeleton
            height={getCssPx(16)}
            radius={getCssPx(4)}
            width={getCssPx(24)}
          />
          <Skeleton
            height={getCssPx(14)}
            variant="text"
            width={getCssPx(index % 3 === 0 ? 128 : 96)}
          />
          <Skeleton
            height={getCssPx(16)}
            radius={getCssPx(4)}
            width={getCssPx(24)}
          />
        </MeasurementDatesAccordionPanelSkeletonRow>
      </MeasurementDatesAccordionPanelSkeletonSection>
    ))}
  </MeasurementDatesAccordionPanelSkeletonList>
);

const MeasurementDatesAccordionPanelStateMessage = ({
  description,
  testId,
  title,
}: {
  description: ReactNode;
  testId: string;
  title: ReactNode;
}) => (
  <MeasurementDatesAccordionPanelState data-testid={testId}>
    <MeasurementDatesAccordionPanelStateTitle>{title}</MeasurementDatesAccordionPanelStateTitle>
    <MeasurementDatesAccordionPanelStateDescription>{description}</MeasurementDatesAccordionPanelStateDescription>
  </MeasurementDatesAccordionPanelState>
);

const MeasurementDatesAccordionPanel = ({
  activeMeasurementDateKey = null,
  canAddFolder = false,
  emptyDescription = MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.emptyDescription,
  emptyTitle = MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.emptyTitle,
  errorDescription = MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.errorDescription,
  errorTitle = MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.errorTitle,
  getMeasurementDateSelectionState,
  hasNewerMeasurementDates = false,
  hasMoreMeasurementDates = false,
  isError = false,
  isDocumentCopyDropTargetEnabled = false,
  isLoading = false,
  isActionInProgress = false,
  isShowMoreLoading = false,
  isShowNewerLoading = false,
  canUploadDocument = false,
  measurementDates,
  onMeasurementDateAddFolder,
  onMeasurementDateSelectionChange,
  onMeasurementDateSelect,
  onMeasurementDateUploadDocument,
  onShowMoreMeasurementDates,
  onShowNewerMeasurementDates,
  pendingMeasurementDateKey = null,
  renderMeasurementDateContent,
  remainingNewerMeasurementDateCount = 0,
  remainingMeasurementDateCount = 0,
  title,
  visibleIncrement = MEASUREMENT_DATES_ACCORDION_PANEL_VISIBLE_INCREMENT,
}: MeasurementDatesAccordionPanelProps) => {
  const { zoomLevel } = useZoom();
  const activeMeasurementDateRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const { handleItemsKeyDown } = useSearchableListKeyboardNavigation<HTMLDivElement>({
    itemSelector: MEASUREMENT_DATES_ACCORDION_PANEL_NAVIGATION_ITEM_SELECTOR,
    listRef,
  });
  const { hiddenItemCounts, scrollToHiddenItems, updateHiddenItemCounts } = useScrollHiddenItemCounts<HTMLDivElement>({
    isEnabled: !isLoading && !isError && measurementDates.length > 0,
    itemSelector: MEASUREMENT_DATES_ACCORDION_PANEL_SCROLL_ITEM_SELECTOR,
    scrollContainerRef: listRef,
  });
  const showMoreCount = Math.min(remainingMeasurementDateCount, visibleIncrement);
  const showNewerCount = Math.min(remainingNewerMeasurementDateCount, visibleIncrement);

  const handleInlineActionClick = (
    event: MouseEvent<HTMLButtonElement>,
    action: (() => void) | undefined
  ) => {
    event.stopPropagation();
    action?.();
  };

  // Keep the active measurement date visible when URL or keyboard navigation changes the opened row.
  useEffect(() => {
    const ownerWindow = activeMeasurementDateRef.current?.ownerDocument.defaultView;

    if (!ownerWindow) {
      return undefined;
    }

    const timeoutId = ownerWindow.setTimeout(() => {
      activeMeasurementDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, MEASUREMENT_DATES_ACCORDION_PANEL_ACTIVE_SCROLL_DELAY_MS);

    return () => {
      ownerWindow.clearTimeout(timeoutId);
    };
  }, [activeMeasurementDateKey]);

  // Recalculate hidden-row hints after pagination or loading state changes the scrollable list.
  useEffect(() => {
    updateHiddenItemCounts();
  }, [
    hasMoreMeasurementDates,
    hasNewerMeasurementDates,
    isShowMoreLoading,
    isShowNewerLoading,
    measurementDates.length,
    updateHiddenItemCounts,
  ]);

  const renderContent = () => {
    if (isLoading) {
      return <MeasurementDatesAccordionPanelSkeleton />;
    }

    if (isError) {
      return (
        <MeasurementDatesAccordionPanelStateMessage
          description={errorDescription}
          testId={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.errorState}
          title={errorTitle}
        />
      );
    }

    if (!measurementDates.length) {
      return (
        <MeasurementDatesAccordionPanelStateMessage
          description={emptyDescription}
          testId={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.emptyState}
          title={emptyTitle}
        />
      );
    }

    return (
      <>
        {hasNewerMeasurementDates && onShowNewerMeasurementDates && showNewerCount > 0 ? (
          <MeasurementDatesAccordionPanelShowMoreButton
            data-measurement-dates-accordion-panel-item="true"
            data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.showNewer}
            loading={isShowNewerLoading}
            loadingBehavior="inline"
            onClick={onShowNewerMeasurementDates}
            placement="top"
            visualVariant="primaryGhost">
            {MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.showNewer(showNewerCount)}
          </MeasurementDatesAccordionPanelShowMoreButton>
        ) : null}
        {measurementDates.map(measurementDate => {
          const measurementDateKey = getMeasurementDateKey(measurementDate);
          const isActive = measurementDateKey === activeMeasurementDateKey;
          const isPending = measurementDateKey === pendingMeasurementDateKey;
          const contentId = MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.content(measurementDateKey);
          const label = getMeasurementDateLabel(measurementDate);
          const metadataLabel = getMeasurementDateMetadataLabel(measurementDate);
          const selectionState = getMeasurementDateSelectionState?.(measurementDate) ?? DEFAULT_SELECTION_STATE;
          const canUseAddFolder = Boolean(
            onMeasurementDateAddFolder && canAddFolder && measurementDate.date && !isPending && !isActionInProgress
          );
          const canUseUploadDocument = Boolean(
            onMeasurementDateUploadDocument && canUploadDocument && measurementDate.date && !isPending && !isActionInProgress
          );
          const isDocumentCopyDropTarget = Boolean(
            isDocumentCopyDropTargetEnabled
              && !isActive
              && !isPending
              && measurementDate.id
              // Firm documents never mix with measurement-date documents.
              && !isFirmLevelDocumentsId(measurementDate.id)
          );

          return (
            <MeasurementDatesAccordionPanelSection key={measurementDateKey} isActive={isActive}>
              <MeasurementDatesAccordionPanelHeader
                aria-controls={contentId}
                aria-busy={isPending || undefined}
                aria-disabled={isPending || undefined}
                aria-expanded={isActive}
                aria-label={
                  isActive
                    ? MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.collapseMeasurementDate(label)
                    : MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.expandMeasurementDate(label)
                }
                data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.item(measurementDateKey)}
                data-firm-documents-measurement-date-copy-target={
                  isDocumentCopyDropTarget ? 'true' : undefined
                }
                data-firm-documents-measurement-date-copy-target-id={
                  isDocumentCopyDropTarget ? measurementDate.id : undefined
                }
                data-firm-documents-measurement-date-copy-target-label={
                  isDocumentCopyDropTarget ? label : undefined
                }
                data-firm-documents-measurement-date-drop-target-label={label}
                data-measurement-dates-accordion-panel-item="true"
                data-measurement-dates-accordion-panel-scroll-item="true"
                isActive={isActive}
                isPending={isPending}
                // Selecting the open row collapses it (the caller toggles), so
                // every date can be closed at once.
                onClick={isPending ? undefined : () => onMeasurementDateSelect?.(measurementDate)}
                onKeyDown={event => {
                  if (
                    (isPending && (event.key === KEYBOARD_ARROW_RIGHT_KEY || isKeyboardActivationKey(event.key)))
                    || (isActive && event.key === KEYBOARD_ARROW_RIGHT_KEY)
                  ) {
                    event.preventDefault();
                    return;
                  }

                  if (event.key === KEYBOARD_ARROW_RIGHT_KEY && !isActive) {
                    event.preventDefault();
                    onMeasurementDateSelect?.(measurementDate);
                    return;
                  }

                  if (isKeyboardActivationKey(event.key)) {
                    event.preventDefault();
                    onMeasurementDateSelect?.(measurementDate);
                  }
                }}
                ref={isActive ? activeMeasurementDateRef : undefined}
                role="button"
                tabIndex={0}>
                <FirmDocumentsSelectionCheckbox
                  checked={selectionState.checked}
                  data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.selectAll(measurementDateKey)}
                  disabled={selectionState.disabled}
                  indeterminate={selectionState.indeterminate}
                  inputProps={{
                    'aria-label': MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.selectMeasurementDateDocuments(label),
                  }}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => {
                    if (isKeyboardActivationKey(event.key)) {
                      event.stopPropagation();
                    }
                  }}
                  onChange={checked => onMeasurementDateSelectionChange?.(measurementDate, checked)}
                />
                <MeasurementDatesAccordionPanelIcon>
                  <FolderOutlined fontSize="inherit" />
                </MeasurementDatesAccordionPanelIcon>
                <OverflowTooltip title={label}>
                  <MeasurementDatesAccordionPanelDate>{label}</MeasurementDatesAccordionPanelDate>
                </OverflowTooltip>
                <OverflowTooltip title={metadataLabel}>
                  <MeasurementDatesAccordionPanelMetadata data-measurement-date-metadata="true">
                    {metadataLabel}
                  </MeasurementDatesAccordionPanelMetadata>
                </OverflowTooltip>
                {onMeasurementDateAddFolder ? (
                  <MeasurementDatesAccordionPanelInlineAction
                    aria-label={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.addSubfolder}
                    data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.addSubfolderButton(measurementDateKey)}
                    disabled={!canUseAddFolder}
                    onClick={event => handleInlineActionClick(
                      event,
                      () => onMeasurementDateAddFolder(measurementDate)
                    )}
                    type="button">
                    <CreateNewFolderOutlined fontSize="inherit" />
                    <span data-measurement-date-inline-action-label="true">
                      {MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.addSubfolder}
                    </span>
                  </MeasurementDatesAccordionPanelInlineAction>
                ) : null}
                {onMeasurementDateUploadDocument ? (
                  <MeasurementDatesAccordionPanelInlineAction
                    aria-label={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.uploadDocument}
                    data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.uploadDocumentButton(measurementDateKey)}
                    disabled={!canUseUploadDocument}
                    onClick={event => handleInlineActionClick(
                      event,
                      () => onMeasurementDateUploadDocument(measurementDate)
                    )}
                    type="button">
                    <UploadFileOutlined fontSize="inherit" />
                    <span data-measurement-date-inline-action-label="true">
                      {MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.uploadDocument}
                    </span>
                  </MeasurementDatesAccordionPanelInlineAction>
                ) : null}
                <MeasurementDatesAccordionPanelChevron isExpanded={isActive}>
                  {isPending ? (
                    <CircularProgress
                      aria-hidden="true"
                      color="inherit"
                      size="var(--measurement-dates-accordion-control-height)"
                      thickness={5}
                    />
                  ) : (
                    <KeyboardArrowDown fontSize="inherit" />
                  )}
                </MeasurementDatesAccordionPanelChevron>
              </MeasurementDatesAccordionPanelHeader>
              <Collapse id={contentId} in={isActive} timeout="auto" unmountOnExit>
                <MeasurementDatesAccordionPanelBody data-testid={contentId}>
                  {renderMeasurementDateContent?.(measurementDate)
                    ?? MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.placeholderDescription}
                </MeasurementDatesAccordionPanelBody>
              </Collapse>
            </MeasurementDatesAccordionPanelSection>
          );
        })}
        {hasMoreMeasurementDates && onShowMoreMeasurementDates && showMoreCount > 0 ? (
          <MeasurementDatesAccordionPanelShowMoreButton
            data-measurement-dates-accordion-panel-item="true"
            data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.showMore}
            loading={isShowMoreLoading}
            loadingBehavior="inline"
            onClick={onShowMoreMeasurementDates}
            placement="bottom"
            visualVariant="primaryGhost">
            {MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.showMore(showMoreCount)}
          </MeasurementDatesAccordionPanelShowMoreButton>
        ) : null}
      </>
    );
  };

  return (
    <MeasurementDatesAccordionPanelRoot
      data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.root}
      zoomLevel={zoomLevel}>
      {title ? (
        <OverflowTooltip title={typeof title === 'string' ? title : ''}>
          <MeasurementDatesAccordionPanelTitle>{title}</MeasurementDatesAccordionPanelTitle>
        </OverflowTooltip>
      ) : null}
      <MeasurementDatesAccordionPanelListFrame>
        {hiddenItemCounts.above > 0 ? (
          <ScrollHiddenItemsHint
            count={hiddenItemCounts.above}
            itemLabelPlural={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.scrollHintItemPlural}
            itemLabelSingular={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.scrollHintItemSingular}
            onClick={() => scrollToHiddenItems('above')}
            placement="top"
          />
        ) : null}
        <MeasurementDatesAccordionPanelList
          ref={listRef}
          data-testid={MEASUREMENT_DATES_ACCORDION_PANEL_TEST_IDS.list}
          onKeyDown={handleItemsKeyDown}
          onScroll={updateHiddenItemCounts}>
          {renderContent()}
        </MeasurementDatesAccordionPanelList>
        {hiddenItemCounts.below > 0 ? (
          <ScrollHiddenItemsHint
            count={hiddenItemCounts.below}
            itemLabelPlural={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.scrollHintItemPlural}
            itemLabelSingular={MEASUREMENT_DATES_ACCORDION_PANEL_LABELS.scrollHintItemSingular}
            onClick={() => scrollToHiddenItems('below')}
            placement="bottom"
          />
        ) : null}
      </MeasurementDatesAccordionPanelListFrame>
    </MeasurementDatesAccordionPanelRoot>
  );
};

export default MeasurementDatesAccordionPanel;
